import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { HRService } from "../../../../config";
import Header from "../../../../components/Header";

const base_url = HRService.uri;

const StatutoryRateForm = (props) => {
  const [loading, setLoading] = useState(false);

  // === IMPORTANT ===
  // We REMOVE the statutoryComponentId field from the UI,
  // but still include it in the payload using props.data.statutoryComponentId.
  

  const initialValues = {
    id: props.data?.id || props.data?.Id || "",
    statutoryComponentId: props.data?.statutoryComponentId || "",
    lowerBound: props.data?.lowerBound || "",
    upperBound: props.data?.upperBound || "",
    rate: props.data?.rate || "",
    taxableAmount: props.data?.taxableAmount || "",
    reliefAmount: props.data?.reliefAmount || "",
    effectiveFrom: props.data?.effectiveFrom || "",
    effectiveTo: props.data?.effectiveTo || "",
  };

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const statutoryComponentData = JSON.parse(localStorage.getItem("StatutoryData") || "{}");
  const statutoryComponentId = statutoryComponentData?.id || null

  const validationSchema = yup.object().shape({
    lowerBound: yup
      .number()
      .min(0, "Lower Bound must be non-negative")
      .required("Lower Bound is required"),

    upperBound: yup
      .number()
      .min(0, "Upper Bound must be non-negative")
      .test(
        "is-greater",
        "Upper Bound must be greater than Lower Bound",
        function (value) {
          const { lowerBound } = this.parent;
          return !value || !lowerBound || value > lowerBound;
        }
      ),

    rate: yup.number().min(0, "Rate must be non-negative"),
    taxableAmount: yup.number().min(0, "Taxable Amount must be non-negative"),
    reliefAmount: yup.number().min(0, "Relief Amount must be non-negative"),

    effectiveFrom: yup
      .string()
      .required("Effective From date is required")
      .matches(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),

    effectiveTo: yup
      .string()
      .matches(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
      .when("effectiveFrom", (from, schema) =>
        from
          ? schema.test(
              "is-after",
              "Effective To must be after Effective From",
              function (value) {
                return !value || new Date(value) >= new Date(from);
              }
            )
          : schema
      ),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        statutoryComponentId: parseInt(statutoryComponentId), // AUTO-INJECTED — removed from form
        lowerBound: parseFloat(values.lowerBound),
        upperBound: values.upperBound ? parseFloat(values.upperBound) : null,
        rate: values.rate ? parseFloat(values.rate) : null,
        taxableAmount: values.taxableAmount
          ? parseFloat(values.taxableAmount)
          : null,
        reliefAmount: values.reliefAmount
          ? parseFloat(values.reliefAmount)
          : null,
        effectiveFrom: values.effectiveFrom,
        effectiveTo: values.effectiveTo || null,
      };

      //  props.isEditing {
      //   payload.id = values.id;
      // };

      const mutationName = props.isEditing
        ? "updateStatutoryRate"
        : "createStatutoryRate";

      const response = await generateAndExecuteMutation(
        mutationName,
        payload,
        "statutoryRate",
        base_url
      );

      if (response) {
        props.onAction?.();
        props.onClose?.();

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Statutory Rate ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Statutory Rate" : "Create Statutory Rate"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Statutory Rate"
            : "Create a New Statutory Rate"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit: formikSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={formikSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 4",
                },
              }}
            >
              {/* LOWER BOUND FIELD (was missing!) */}
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Lower Bound *"
                name="lowerBound"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.lowerBound}
                error={touched.lowerBound && !!errors.lowerBound}
                helperText={touched.lowerBound && errors.lowerBound}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Upper Bound"
                name="upperBound"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.upperBound}
                error={touched.upperBound && !!errors.upperBound}
                helperText={touched.upperBound && errors.upperBound}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Rate (%)"
                name="rate"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.rate}
                error={touched.rate && !!errors.rate}
                helperText={touched.rate && errors.rate}
                sx={{ gridColumn: "span 2" }}
              />

              {/* FIXED taxableAmount BUGS */}
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Taxable Amount"
                name="taxableAmount"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.taxableAmount}
                error={touched.taxableAmount && !!errors.taxableAmount}
                helperText={touched.taxableAmount && errors.taxableAmount}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Relief Amount"
                name="reliefAmount"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.reliefAmount}
                error={touched.reliefAmount && !!errors.reliefAmount}
                helperText={touched.reliefAmount && errors.reliefAmount}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="Effective From *"
                name="effectiveFrom"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.effectiveFrom}
                error={touched.effectiveFrom && !!errors.effectiveFrom}
                helperText={touched.effectiveFrom && errors.effectiveFrom}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="Effective To"
                name="effectiveTo"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.effectiveTo}
                error={touched.effectiveTo && !!errors.effectiveTo}
                helperText={touched.effectiveTo && errors.effectiveTo}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default StatutoryRateForm;
